/**
 * @module public/js/utils/focusModeCallMode.js
 * @summary Detect whether Focus Mode should use browser telephony or a native phone app.
 */

/**
 * Supported Focus Mode call surfaces.
 *
 * @type {{ BROWSER: 'browser', NATIVE_PHONE: 'native-phone' }}
 */
export const FOCUS_MODE_CALL_MODE = {
  BROWSER: 'browser',
  NATIVE_PHONE: 'native-phone',
};

/**
 * Determine whether the supplied user agent represents a smartphone browser.
 *
 * @param {string} [userAgent=''] - Browser user agent string.
 * @returns {boolean} True when the user agent looks like an iPhone/iPod or Android phone browser.
 */
export function isSmartphoneUserAgent(userAgent = '') {
  const ua = String(userAgent || '');
  const isiPhone = /iPhone|iPod/i.test(ua);
  const isAndroidPhone = /Android/i.test(ua) && /Mobile/i.test(ua);
  return isiPhone || isAndroidPhone;
}

/**
 * Resolve the smallest available screen dimension for the current browser context.
 *
 * Prefers physical screen metrics because mobile browsers can report wide layout
 * viewports when "desktop site" mode is enabled even on a phone.
 *
 * @param {object} [options={}] - Optional environment overrides for tests.
 * @param {Window & typeof globalThis} [options.windowObject] - Window-like object supplying screen/viewport metrics.
 * @returns {number} Smallest positive CSS-pixel dimension or `0` when unavailable.
 */
export function resolveSmallestScreenDimension({ windowObject } = {}) {
  const win =
    windowObject ||
    (typeof window !== 'undefined' ? window : undefined) ||
    undefined;
  const screenObject =
    win?.screen || (typeof screen !== 'undefined' ? screen : undefined);
  const screenDims = [screenObject?.width, screenObject?.height]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (screenDims.length > 0) {
    return Math.min(...screenDims);
  }

  const viewportDims = [
    win?.visualViewport?.width,
    win?.visualViewport?.height,
    win?.innerWidth,
    win?.innerHeight,
  ]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  return viewportDims.length > 0 ? Math.min(...viewportDims) : 0;
}

/**
 * Resolve the smallest layout viewport dimension for the current browser context.
 *
 * Useful when a mobile browser requests a desktop site and inflates its screen
 * metrics while still keeping a phone-sized viewport.
 *
 * @param {object} [options={}] - Optional environment overrides for tests.
 * @param {Window & typeof globalThis} [options.windowObject] - Window-like object supplying viewport metrics.
 * @returns {number} Smallest positive viewport dimension or `0` when unavailable.
 */
export function resolveSmallestViewportDimension({ windowObject } = {}) {
  const win =
    windowObject ||
    (typeof window !== 'undefined' ? window : undefined) ||
    undefined;
  const viewportDims = [
    win?.visualViewport?.width,
    win?.visualViewport?.height,
    win?.innerWidth,
    win?.innerHeight,
  ]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  return viewportDims.length > 0 ? Math.min(...viewportDims) : 0;
}

/**
 * Determine whether the current environment looks like iPhone-class hardware
 * even when the browser is requesting a desktop site.
 *
 * @param {object} [options={}] - Optional detection overrides for tests.
 * @param {Navigator} [options.navigatorObject] - Navigator-like object supplying platform metadata.
 * @param {Window & typeof globalThis} [options.windowObject] - Window-like object supplying viewport metrics.
 * @returns {boolean} True when the environment looks like a physical iPhone/iPod.
 */
export function isApplePhoneHardware({ navigatorObject, windowObject } = {}) {
  const nav =
    navigatorObject ||
    (typeof window !== 'undefined' ? window.navigator : undefined) ||
    (typeof navigator !== 'undefined' ? navigator : undefined);
  const platform = String(nav?.platform || '');
  const maxTouchPoints = Number(nav?.maxTouchPoints ?? 0);
  const smallestViewportDimension = resolveSmallestViewportDimension({
    windowObject,
  });

  return (
    /iPhone|iPod/i.test(platform) ||
    (/Mac/i.test(platform) &&
      maxTouchPoints > 0 &&
      smallestViewportDimension > 0 &&
      smallestViewportDimension <= 430)
  );
}

/**
 * Determine whether the current environment looks like a physical smartphone
 * even when the browser user agent is unhelpful.
 *
 * @param {object} [options={}] - Optional detection overrides for tests.
 * @param {Navigator} [options.navigatorObject] - Navigator-like object supplying touch metadata.
 * @param {Window & typeof globalThis} [options.windowObject] - Window-like object supplying screen/viewport metrics.
 * @returns {boolean} True when the environment looks like a small touch-screen device.
 */
export function isPhoneSizedTouchDevice({
  navigatorObject,
  windowObject,
} = {}) {
  const nav =
    navigatorObject ||
    (typeof window !== 'undefined' ? window.navigator : undefined) ||
    (typeof navigator !== 'undefined' ? navigator : undefined);
  const maxTouchPoints = Number(nav?.maxTouchPoints ?? 0);
  const smallestDimension = resolveSmallestScreenDimension({ windowObject });

  return (
    maxTouchPoints > 0 && smallestDimension > 0 && smallestDimension <= 540
  );
}

/**
 * Resolve whether Focus Mode should use the browser dialer flow or the native phone app.
 *
 * @param {object} [options={}] - Optional detection overrides for tests.
 * @param {string} [options.userAgent] - Explicit user agent string.
 * @param {Navigator} [options.navigatorObject] - Navigator-like object to read from.
 * @param {Window & typeof globalThis} [options.windowObject] - Window-like object to read screen metrics from.
 * @returns {'browser'|'native-phone'} The preferred Focus Mode call surface.
 */
export function resolveFocusModeCallMode({
  userAgent,
  navigatorObject,
  windowObject,
} = {}) {
  const nav =
    navigatorObject ||
    (typeof window !== 'undefined' ? window.navigator : undefined) ||
    (typeof navigator !== 'undefined' ? navigator : undefined);
  const resolvedUserAgent = userAgent ?? nav?.userAgent ?? '';
  const userAgentDataMobile = nav?.userAgentData?.mobile === true;

  return userAgentDataMobile ||
    isSmartphoneUserAgent(resolvedUserAgent) ||
    isApplePhoneHardware({ navigatorObject: nav, windowObject }) ||
    isPhoneSizedTouchDevice({ navigatorObject: nav, windowObject })
    ? FOCUS_MODE_CALL_MODE.NATIVE_PHONE
    : FOCUS_MODE_CALL_MODE.BROWSER;
}
